import React from "react";

import { FieldProps } from "../../../types/FormTypes";
import Field from "../Field/Field";
import MultiSelect, { type MultiSelectProps } from "../MultiSelect/MultiSelect";
import { type SelectOptions } from "../Select/Select";
import { useField } from "../Form/Form";

export interface MultiSelectFieldProps
  extends Omit<FieldProps<string[]>, "onValueChange" | "value" | "error">,
    Omit<MultiSelectProps, "value" | "onValueChange" | "name" | "options" | "placeholder" | "disabled" | "fullWidth" | "error"> {
  error?: boolean | string;
  value?: string[];
  onValueChange?: (value: string[]) => void;
  options: SelectOptions;
  placeholder?: string;
  allowArbitrary?: boolean;
}

const MultiSelectField = ({
  name,
  label,
  options,
  helperText,
  error,
  required,
  disabled,
  fullWidth = false,
  value: controlledValue,
  onValueChange,
  placeholder,
  className,
  ...props
}: MultiSelectFieldProps) => {
  const field = useField<string[]>({ name, value: controlledValue, onValueChange });
  const value = field.value ?? [];
  const errorMessage = typeof error === "string" ? error : undefined;
  const hasError = Boolean(field.error || errorMessage || (typeof error === "boolean" ? error : false));

  return (
    <Field
      className={className}
      name={name}
      fullWidth={fullWidth}
    >
      {label ? <Field.Label required={required}>{label}</Field.Label> : null}
      <Field.Control>
        <MultiSelect
          ref={field.ref as any}
          name={name}
          value={Array.isArray(value) ? value : []}
          options={options}
          onValueChange={field.onValueChange}
          onBlur={field.onBlur}
          placeholder={placeholder || (label ? `Select ${label}` : "Select...")}
          disabled={disabled}
          fullWidth={fullWidth}
          error={hasError}
          {...props}
        />
      </Field.Control>
      <Field.Message
        error={hasError}
        message={errorMessage ?? helperText}
      />
    </Field>
  );
};

export default MultiSelectField;
